/**
 * Represents a function that performs an event and returns a Promise.
 */
type EventFunction = () => Promise<void>

/**
 * Record for deduplication, storing the execution timestamp.
 */
type DeduplicationRecord = {
  executedAt: number
}

/**
 * Options for event deduplication, including retry logic and flushing.
 */
type DeduplicationOptions = {
  /** Maximum number of retries for the event function. */
  maxRetries?: number
  /** Delay in milliseconds between retries. */
  retryDelayMs?: number
  /** How long (ms) to retain executed events in memory. */
  retentionTimeMs?: number
  /** Maximum number of executed events to keep in memory. */
  maxRecords?: number
  /** How often (ms) to flush old events. 0 to disable. */
  flushIntervalMs?: number
  /** Maximum time (ms) to wait for an in-flight event before considering it stalled. */
  inFlightTimeoutMs?: number
}

/**
 * EventDeduplicator ensures that a given event (by key) is only executed once, with optional retry logic and memory management.
 */
export class EventDeduplicator {
  private executedEvents: Map<string, DeduplicationRecord>
  private options: Required<DeduplicationOptions>
  private inFlightEvents: Map<string, Promise<void>>
  private flusherIntervalId?: ReturnType<typeof setInterval>

  constructor(options: DeduplicationOptions = {}) {
    this.executedEvents = new Map<string, DeduplicationRecord>()
    this.inFlightEvents = new Map<string, Promise<void>>()
    this.options = {
      maxRetries: options.maxRetries || 3,
      retryDelayMs: options.retryDelayMs || 1000,
      retentionTimeMs: options.retentionTimeMs || 5 * 60 * 1000, // 5 minutes default
      maxRecords: options.maxRecords || 100,
      flushIntervalMs: options.flushIntervalMs || 60 * 1000, // every 1 min
      inFlightTimeoutMs: options.inFlightTimeoutMs || 30 * 1000, // 30 seconds default
    }

    if (this.options.flushIntervalMs > 0) {
      this.flusherIntervalId = setInterval(
        () => this.flushOldEvents(),
        this.options.flushIntervalMs
      )
    }
  }

  /**
   * Flushes executed events that are older than retentionTimeMs.
   */
  private flushOldEvents() {
    const now = Date.now()
    for (const [key, record] of this.executedEvents.entries()) {
      if (now - record.executedAt > this.options.retentionTimeMs) {
        this.executedEvents.delete(key)
      }
    }
  }

  /**
   * Ensures the executedEvents map does not exceed maxRecords by removing oldest entries.
   */
  private flushExcessEvents() {
    while (this.executedEvents.size > this.options.maxRecords) {
      const oldestKey = this.executedEvents.keys().next().value

      if (oldestKey !== undefined) {
        this.executedEvents.delete(oldestKey)
      } else {
        break
      }
    }
  }

  /**
   * Generates a unique deduplication key from the provided arguments.
   * @param args Strings to be joined into a deduplication key.
   * @returns The generated deduplication key.
   */
  static generateEventKey(...args: string[]) {
    return args.join('|')
  }

  /**
   * Executes the provided event function only once for the given deduplication key.
   * If the event is already in progress or has been executed, it will not run again.
   * Retries the event function on failure according to the configured options.
   *
   * @param eventFunction The asynchronous event function to execute.
   * @param deduplicationKey The unique key identifying the event.
   * @param onError Optional callback that will be called with the error if the event function fails after all retries. If not provided, a warning will be logged to the console.
   * @returns A promise that resolves when the event has been executed or skipped.
   */
  async executeOnce(
    eventFunction: EventFunction,
    deduplicationKey: string,
    onError?: (error: Error) => void
  ): Promise<void> {
    try {
      const executedAt = Date.now()
      const record = this.executedEvents.get(deduplicationKey)

      if (record) {
        return Promise.resolve()
      }

      if (this.inFlightEvents.has(deduplicationKey)) {
        return this.inFlightEvents.get(deduplicationKey)!
      }

      const eventPromise = this.runWithTimeout(
        this.runWithRetries(eventFunction, deduplicationKey, executedAt),
        this.options.inFlightTimeoutMs,
        deduplicationKey
      )
      this.inFlightEvents.set(deduplicationKey, eventPromise)

      try {
        await eventPromise
      } finally {
        this.inFlightEvents.delete(deduplicationKey)
      }
    } catch (error) {
      if (onError) {
        onError(error)
      } else {
        console.warn('Event execution failed:', { error, deduplicationKey })
      }
    }
  }

  /**
   * Runs the event function with retry logic.
   * @param eventFunction The asynchronous event function to execute.
   * @param key The deduplication key for the event.
   * @param executedAt The timestamp when the event is considered executed.
   * @returns A promise that resolves when the event has been executed successfully.
   * @throws If the event function fails after the maximum number of retries.
   */
  private async runWithRetries(
    eventFunction: EventFunction,
    key: string,
    executedAt: number
  ): Promise<void> {
    let attempts = 0
    while (attempts < this.options.maxRetries) {
      try {
        await eventFunction()

        this.recordExecution(key, executedAt)

        return
      } catch (error) {
        attempts++
        if (attempts >= this.options.maxRetries) {
          throw new Error(`Failed to execute event after ${this.options.maxRetries} attempts`)
        }
        await this.delay(this.options.retryDelayMs)
      }
    }
  }

  /**
   * Records the execution
   * @param key The deduplication key for the event.
   * @param executedAt The timestamp when the event was executed.
   */
  private recordExecution(key: string, executedAt: number): void {
    const record: DeduplicationRecord = { executedAt }
    this.executedEvents.set(key, record)
    this.flushExcessEvents()
  }

  /**
   * Delays execution for a specified number of milliseconds.
   * @param ms The number of milliseconds to delay.
   * @returns A promise that resolves after the delay.
   */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }

  /**
   * Wraps a promise with a timeout. If the promise does not settle within timeoutMs, it rejects and cleans up inFlightEvents.
   */
  private runWithTimeout<T>(
    promise: Promise<T>,
    timeoutMs: number,
    deduplicationKey: string
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.inFlightEvents.delete(deduplicationKey)
        reject(new Error(`Event execution timed out after ${timeoutMs} ms`))
      }, timeoutMs)
      promise.then(
        (value) => {
          clearTimeout(timeout)
          resolve(value)
        },
        (err) => {
          clearTimeout(timeout)
          reject(err)
        }
      )
    })
  }

  /**
   * Stops the periodic flusher that removes old events from memory.
   * This method clears the interval timer and resets the flusher interval ID.
   */
  public stopFlusher() {
    if (this.flusherIntervalId) {
      clearInterval(this.flusherIntervalId)
      this.flusherIntervalId = undefined
    }
  }
}
